import { Helmet } from 'react-helmet-async';

interface SpeakableSchemaProps {
  /** CSS selectors pointing to speakable content sections */
  cssSelectors?: string[];
  /** XPath expressions pointing to speakable content */
  xpaths?: string[];
  /** URL of the page */
  url: string;
  /** Headline/Title of the article or page */
  headline: string;
  /** Date of publication */
  datePublished?: string;
  /** Author of the content */
  author?: string;
}

/**
 * SpeakableSpecification Schema Component
 * Marks content suitable for voice search and text-to-speech
 * Helps Google Assistant, Alexa, and other voice assistants read content aloud
 */
const SpeakableSchema: React.FC<SpeakableSchemaProps> = ({
  cssSelectors = ['.speakable-intro', '.speakable-summary', 'article p:first-of-type'],
  xpaths,
  url,
  headline,
  datePublished,
  author = 'Schoolola'
}) => {
  const speakable: Record<string, unknown> = {
    "@type": "SpeakableSpecification"
  };

  if (cssSelectors.length > 0) {
    speakable["cssSelector"] = cssSelectors;
  }

  if (xpaths && xpaths.length > 0) {
    speakable["xpath"] = xpaths;
  }

  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": headline,
    "url": url,
    "speakable": speakable,
    "publisher": {
      "@type": "Organization",
      "name": "Schoolola",
      "logo": {
        "@type": "ImageObject",
        "url": "https://www.schoolola.com.au/lovable-uploads/8577c389-5ccd-44bd-b04f-8b7121ad91d6.png"
      }
    }
  };

  if (datePublished) {
    schema["datePublished"] = datePublished;
  }

  if (author) {
    schema["author"] = {
      "@type": "Person",
      "name": author
    };
  }

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

export default SpeakableSchema;

// Pre-configured for blog posts
export const BlogSpeakableSchema: React.FC<{
  headline: string;
  slug: string;
  author?: string;
  datePublished?: string;
}> = ({
  headline,
  slug,
  author = 'Schoolola',
  datePublished
}) => (
  <SpeakableSchema
    headline={headline}
    url={`https://www.schoolola.com.au/newsroom/${slug}`}
    author={author}
    datePublished={datePublished}
    cssSelectors={[
      '.prose p:first-of-type',
      '.text-xl.text-muted-foreground',
      'article .mb-12 > p:first-child'
    ]}
  />
);

// Pre-configured for program pages
export const ProgramSpeakableSchema: React.FC<{
  programName: string;
  slug: string;
}> = ({
  programName,
  slug
}) => (
  <SpeakableSchema
    headline={`${programName} - Schoolola Tutoring`}
    url={`https://www.schoolola.com.au/programs/${slug}`}
    cssSelectors={[
      'section p.text-lg',
      '.prose p:first-of-type',
      'article p:first-of-type'
    ]}
  />
);
